/**
 * Login and authentication related APIs
 */
import { http } from '@/utils';
import type { RequestOptions } from '@/utils';

// ==================== API Endpoints ====================
// Note: URLs are relative paths, API_PREFIX will be added automatically by http util
const Api = {
  Login: '/Login',
  GetAuth: '/GetAuth',
  FakeCaptcha: '/api/login/captcha',
};

// ==================== API Functions ====================

/**
 * Get fake captcha - returns mock captcha code
 */
export async function getFakeCaptcha(
  params: { phone?: string },
  options?: RequestOptions,
): Promise<API.FakeCaptcha> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Return mock captcha (in real scenario, this would be sent via SMS)
  console.log(`[Mock] Sending captcha to phone: ${params.phone}`);
  return { code: 200, status: 'ok' };
}

/**
 * Login - calls backend API to authenticate user
 */
export async function login(params: API.LoginParams): Promise<API.LoginResult> {
  const response = await http.post<API.LoginResult>(Api.Login, params);
  return response?.Data || {};
}

/**
 * Get current user - calls GetAuth API to fetch user info
 */
export async function currentUser(options?: RequestOptions): Promise<{
  data: API.CurrentUser;
}> {
  // First check local cache
  const cachedInfo = getUserInfo();
  if (cachedInfo) {
    return { data: cachedInfo };
  }

  // No cache, call GetAuth API to fetch user info
  try {
    const response = await http.get<API.CurrentUser>(Api.GetAuth, options);

    console.log('GetAuth response:', response);

    // Cache user info locally
    saveUserInfo(response.Data);

    return { data: response.Data };
  } catch (error: any) {
    console.error('GetAuth failed:', error);
    // Clear any stale data
    clearUserInfo();
    throw error;
  }
}

// ==================== Local Storage Helpers ====================

const USER_INFO_KEY = 'userInfo';

/**
 * Save user info to storage
 */
export function saveUserInfo(userInfo: API.CurrentUser): void {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
}

/**
 * Get user info from storage
 */
export function getUserInfo(): API.CurrentUser | null {
  const data = localStorage.getItem(USER_INFO_KEY);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      localStorage.removeItem(USER_INFO_KEY);
    }
  }
  return null;
}

/**
 * Clear user info from storage
 */
export function clearUserInfo(): void {
  localStorage.removeItem(USER_INFO_KEY);
}
